/**
 * Leave & Holidays — the modern replacement for the old Time Off page.
 *
 * Route stays `/time-off-requests` so existing links keep working; the sidebar
 * label and page title now read "Leave & Holidays". Historic time-off data is
 * still shown because the new backend reads from the same collections and just
 * layers the new balance/entitlement fields on top.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Umbrella, Plus, Check, X, CalendarDays, ClipboardList, Users, ShieldAlert,
  Info, AlertTriangle, PartyPopper, HelpCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
  pending:         "bg-amber-50 border-amber-300 text-amber-800",
  approved:        "bg-emerald-50 border-emerald-300 text-emerald-800",
  rejected:        "bg-red-50 border-red-300 text-red-800",
  cancelled:       "bg-slate-100 border-slate-300 text-slate-700",
  info_requested:  "bg-sky-50 border-sky-300 text-sky-800",
};

const fmtDay = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function StatCard({ label, value, unit, tone = "slate", testid, sub }) {
  const toneClass = {
    slate:   "bg-white border-slate-200",
    ok:      "bg-emerald-50 border-emerald-300",
    warn:    "bg-amber-50 border-amber-300",
    info:    "bg-sky-50 border-sky-300",
    accent:  "bg-slate-900 border-slate-900 text-white",
  }[tone];
  return (
    <div className={`${toneClass} border p-4`} data-testid={testid}>
      <div className={`text-3xl font-heading font-semibold ${tone === "accent" ? "text-white" : "text-slate-900"}`}>{value}</div>
      <div className={`text-[11px] uppercase tracking-widest ${tone === "accent" ? "text-slate-300" : "text-slate-500"} mt-1 font-semibold`}>{label} <span className="lowercase font-normal">· {unit}</span></div>
      {sub && <div className={`text-[11px] mt-1 ${tone === "accent" ? "text-slate-200" : "text-slate-600"}`}>{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${STATUS_STYLE[status] || "bg-slate-100 border-slate-300 text-slate-700"}`} data-testid={`status-${status}`}>
      {status.replace("_", " ")}
    </span>
  );
}

export default function TimeOffRequests() {
  const { user } = useAuth();
  const canApprove = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState("my-leave");
  const [balance, setBalance] = useState(null);
  const [rows, setRows] = useState([]);
  const [teamBalances, setTeamBalances] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // request dialog state
  const [reqOpen, setReqOpen] = useState(false);
  const emptyForm = { leave_type: "annual", start_date: "", end_date: "", half_day: false, half_day_period: "am", reason: "" };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // decision dialog state (approve / reject / info)
  const [decision, setDecision] = useState({ open: false, action: "approve", req: null, notes: "", conflicts: null, override: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [api.get("/leave/balance"), api.get("/leave/requests"), api.get("/leave/settings")];
      if (canApprove) promises.push(api.get("/leave/team-balances"));
      const [b, r, s, tb] = await Promise.all(promises);
      setBalance(b.data);
      setRows(r.data || []);
      setTypes(s.data.leave_types || []);
      if (tb) setTeamBalances(tb.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [canApprove]);
  useEffect(() => { load(); }, [load]);

  // Auto-compute a projected working-days preview in the request dialog
  const requestedDays = useMemo(() => {
    if (!form.start_date || !form.end_date) return 0;
    if (form.half_day && form.start_date === form.end_date) return 0.5;
    const s = new Date(form.start_date + "T00:00");
    const e = new Date(form.end_date + "T00:00");
    if (e < s) return 0;
    const diff = Math.round((e - s) / 86400000) + 1;
    return diff;
  }, [form]);

  const submit = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Start and end date are required"); return; }
    if (form.half_day && form.start_date !== form.end_date) { toast.error("Half-day leave must be for a single date"); return; }
    setSaving(true);
    try {
      await api.post("/leave/requests", { ...form, working_days: requestedDays });
      toast.success("Leave request submitted");
      setReqOpen(false);
      setForm(emptyForm);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const cancelReq = async (r) => {
    if (!window.confirm(`Cancel this ${r.leave_type || r.kind} request?`)) return;
    try {
      await api.post(`/leave/requests/${r.id}/cancel`);
      toast.success("Request cancelled");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDecision = async (r, action) => {
    let conflicts = null;
    if (action === "approve") {
      try {
        const c = await api.get(`/leave/rota-conflict`, { params: { staff_user_id: r.staff_user_id, start_date: r.start_date, end_date: r.end_date } });
        conflicts = c.data;
      } catch (e) { conflicts = { count: 0, shifts: [] }; }
    }
    setDecision({ open: true, action, req: r, notes: "", conflicts, override: false });
  };

  const submitDecision = async () => {
    const { action, req, notes, override } = decision;
    try {
      if (action === "approve") {
        await api.post(`/leave/requests/${req.id}/approve`, { notes, override_insufficient: override });
      } else if (action === "reject") {
        await api.post(`/leave/requests/${req.id}/reject`, { notes });
      } else if (action === "info") {
        if (!notes.trim()) { toast.error("Ask a question"); return; }
        await api.post(`/leave/requests/${req.id}/request-info`, { question: notes });
      }
      toast.success(action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Info requested");
      setDecision({ open: false, action: "approve", req: null, notes: "", conflicts: null, override: false });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const myRows = rows.filter((r) => r.staff_user_id === user?.id);
  const pendingApprovals = rows.filter((r) => r.staff_user_id !== user?.id && r.status === "pending");
  const upcomingBooked = myRows
    .filter((r) => r.status === "approved" && (r.end_date || "") >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));

  const progressPct = balance && balance.total_entitlement > 0
    ? Math.min(100, Math.round(((balance.taken + balance.booked) / balance.total_entitlement) * 100))
    : 0;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="hidden lg:flex h-14 border-b border-slate-200 bg-white px-6 items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Umbrella className="w-3 h-3" /> Care Delivery</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="leave-title">Leave &amp; Holidays</h1>
        </div>
        <Button size="sm" onClick={() => setReqOpen(true)} data-testid="btn-new-request" className="h-9 bg-slate-900 hover:bg-slate-800">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Request leave
        </Button>
      </div>
      {/* Mobile header — Batch 7D */}
      <div className="lg:hidden border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Umbrella className="w-3 h-3" /> Care Delivery</div>
          <h1 className="font-heading text-base font-semibold truncate" data-testid="leave-title-mobile">Leave &amp; Holidays</h1>
        </div>
        <Button size="sm" onClick={() => setReqOpen(true)} data-testid="btn-new-request-mobile" className="h-11 bg-slate-900 hover:bg-slate-800 shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Request
        </Button>
      </div>

      <div className="p-4 lg:p-6 space-y-6">
        {loading || !balance ? (
          <div className="text-sm text-slate-500" data-testid="leave-loading">Loading leave dashboard…</div>
        ) : (
          <>
            {/* Balance hero */}
            <section className="space-y-3" data-testid="leave-hero">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">My Holiday · {new Date(balance.holiday_year_start).getFullYear()}</div>
                  <div className="text-sm text-slate-600">Holiday year runs {balance.holiday_year_start} → {balance.holiday_year_end}</div>
                </div>
                {balance.next_holiday && (
                  <div className="text-xs bg-emerald-50 border border-emerald-300 text-emerald-800 px-2 py-1 inline-flex items-center gap-1" data-testid="next-holiday">
                    <PartyPopper className="w-3 h-3" /> Next holiday · {balance.next_holiday.start_date} → {balance.next_holiday.end_date}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatCard testid="stat-entitlement" label="Annual entitlement" value={fmtDay(balance.annual_entitlement)} unit={balance.unit} />
                <StatCard testid="stat-carried-over" label="Carried over" value={fmtDay(balance.carried_over)} unit={balance.unit} />
                <StatCard testid="stat-taken" label="Taken" value={fmtDay(balance.taken)} unit={balance.unit} />
                <StatCard testid="stat-booked" label="Booked / approved" value={fmtDay(balance.booked)} unit={balance.unit} />
                <StatCard testid="stat-pending" label="Pending" value={fmtDay(balance.pending)} unit={balance.unit} tone="warn" />
                <StatCard
                  testid="stat-available"
                  label="Available to book"
                  value={fmtDay(balance.available)}
                  unit={balance.unit}
                  tone="accent"
                  sub={balance.pending > 0 ? `${fmtDay(balance.projected_available)} ${balance.unit} if pending is approved` : null}
                />
              </div>
              <div className="bg-white border border-slate-200 p-3">
                <div className="flex items-center justify-between text-xs text-slate-600">
                  <span>{fmtDay(balance.taken + balance.booked)} of {fmtDay(balance.total_entitlement)} {balance.unit} taken/booked</span>
                  <span className="font-medium">{progressPct}%</span>
                </div>
                <div className="mt-2 h-2 w-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-slate-900" style={{ width: `${progressPct}%` }} data-testid="progress-bar" />
                </div>
              </div>
            </section>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="flex flex-wrap gap-1 bg-slate-100 p-1">
                <TabsTrigger value="my-leave" data-testid="tab-my-leave">My Leave</TabsTrigger>
                <TabsTrigger value="request" data-testid="tab-request">Request Leave</TabsTrigger>
                <TabsTrigger value="calendar" data-testid="tab-calendar">Leave Calendar</TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history">Leave History</TabsTrigger>
                {canApprove && <TabsTrigger value="approvals" data-testid="tab-approvals">Approvals{pendingApprovals.length > 0 && <span className="ml-1 text-[10px] px-1 bg-amber-500 text-white rounded">{pendingApprovals.length}</span>}</TabsTrigger>}
                {canApprove && <TabsTrigger value="team-calendar" data-testid="tab-team-calendar">Team Calendar</TabsTrigger>}
                {canApprove && <TabsTrigger value="balances" data-testid="tab-balances">Leave Balances</TabsTrigger>}
              </TabsList>

              {/* MY LEAVE */}
              <TabsContent value="my-leave" className="pt-4" data-testid="tabc-my-leave">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-slate-200 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Upcoming approved holidays</div>
                    {upcomingBooked.length === 0 ? (
                      <div className="text-sm text-slate-500">No approved future leave.</div>
                    ) : (
                      <ul className="space-y-2 text-sm">
                        {upcomingBooked.map((r) => (
                          <li key={r.id} className="border-l-2 border-emerald-500 pl-3">
                            <div className="font-medium text-slate-900">{r.start_date} → {r.end_date}</div>
                            <div className="text-xs text-slate-500 capitalize">{(r.leave_type || r.kind || "").replace(/_/g, " ")} · {fmtDay(r.working_days)} {balance.unit}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="bg-white border border-slate-200 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Pending requests</div>
                    {myRows.filter((r) => r.status === "pending" || r.status === "info_requested").length === 0 ? (
                      <div className="text-sm text-slate-500">No pending requests.</div>
                    ) : (
                      <ul className="space-y-2 text-sm">
                        {myRows.filter((r) => r.status === "pending" || r.status === "info_requested").map((r) => (
                          <li key={r.id} className="border-l-2 border-amber-500 pl-3">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-slate-900">{r.start_date} → {r.end_date}</div>
                              <StatusBadge status={r.status} />
                            </div>
                            <div className="text-xs text-slate-500 capitalize">{(r.leave_type || r.kind || "").replace(/_/g, " ")} · {fmtDay(r.working_days)} {balance.unit}</div>
                            {r.info_note && (
                              <div className="text-xs bg-sky-50 border border-sky-200 text-sky-800 mt-1 p-1.5 inline-flex items-start gap-1"><HelpCircle className="w-3 h-3 mt-0.5 shrink-0" /> {r.info_note}</div>
                            )}
                            <Button size="sm" variant="outline" className="h-7 text-xs mt-2 text-red-700" onClick={() => cancelReq(r)} data-testid={`btn-cancel-${r.id}`}>Cancel request</Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* REQUEST LEAVE (inline copy — dialog also opens via button) */}
              <TabsContent value="request" className="pt-4" data-testid="tabc-request">
                <div className="bg-white border border-slate-200 p-6 max-w-3xl">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Submit a new leave request</div>
                  <div className="text-sm text-slate-600 mb-4">Tap the button to open the request form. AYOBELL will automatically calculate the number of working days and flag any rota conflicts to your manager.</div>
                  <Button onClick={() => setReqOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-open-request-form">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Open request form
                  </Button>
                </div>
              </TabsContent>

              {/* LEAVE CALENDAR — simple month grid */}
              <TabsContent value="calendar" className="pt-4" data-testid="tabc-calendar">
                <LeaveCalendarPanel scope={canApprove ? "team" : "mine"} />
              </TabsContent>

              {/* LEAVE HISTORY */}
              <TabsContent value="history" className="pt-4" data-testid="tabc-history">
                <div className="bg-white border border-slate-200 overflow-x-auto">
                  <table className="min-w-full text-xs" data-testid="history-table">
                    <thead className="bg-slate-50 text-slate-600 uppercase tracking-widest text-[10px]">
                      <tr>
                        <th className="px-3 py-2 text-left">Leave type</th>
                        <th className="px-3 py-2 text-left">Start</th>
                        <th className="px-3 py-2 text-left">End</th>
                        <th className="px-3 py-2 text-right">Days / hours</th>
                        <th className="px-3 py-2 text-left">Requested</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Decided by</th>
                        <th className="px-3 py-2 text-left">Decision date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myRows.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100" data-testid={`history-row-${r.id}`}>
                          <td className="px-3 py-2 capitalize">{(r.leave_type || r.kind || "").replace(/_/g, " ")}</td>
                          <td className="px-3 py-2">{r.start_date}</td>
                          <td className="px-3 py-2">{r.end_date}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtDay(balance.unit === "hours" ? r.hours : r.working_days)}</td>
                          <td className="px-3 py-2">{(r.created_at || "").slice(0, 10)}</td>
                          <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                          <td className="px-3 py-2">{r.decided_by_name || r.decided_by || "—"}</td>
                          <td className="px-3 py-2">{r.decided_at ? new Date(r.decided_at).toLocaleDateString() : "—"}</td>
                        </tr>
                      ))}
                      {myRows.length === 0 && (
                        <tr><td colSpan={8} className="px-3 py-4 text-center text-slate-500">No leave history yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* APPROVALS (manager+) */}
              {canApprove && (
                <TabsContent value="approvals" className="pt-4" data-testid="tabc-approvals">
                  {pendingApprovals.length === 0 ? (
                    <div className="bg-white border border-slate-200 p-6 text-sm text-slate-500" data-testid="approvals-empty">No leave requests currently need your decision.</div>
                  ) : (
                    <ul className="space-y-2" data-testid="approvals-list">
                      {pendingApprovals.map((r) => {
                        const tb = teamBalances.find((b) => b.staff_user_id === r.staff_user_id);
                        const qty = tb?.unit === "hours" ? r.hours : r.working_days;
                        const after = tb ? +(tb.available - (qty || 0)).toFixed(2) : null;
                        return (
                          <li key={r.id} className="bg-white border border-slate-200 p-4" data-testid={`approval-row-${r.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-900">{r.staff_name || r.staff_email} <span className="text-xs text-slate-500 ml-2">{r.site_name || ""}</span></div>
                                <div className="text-xs text-slate-600 mt-1 capitalize">{(r.leave_type || r.kind || "").replace(/_/g, " ")} · {r.start_date} → {r.end_date} · {fmtDay(qty)} {tb?.unit || "days"}</div>
                                {r.reason && <div className="text-xs text-slate-500 mt-1">Reason: {r.reason}</div>}
                                {tb && (
                                  <div className="mt-2 text-[11px] text-slate-600 grid grid-cols-3 gap-2 max-w-md" data-testid={`approval-balance-${r.id}`}>
                                    <span>Current: <b>{fmtDay(tb.available)}</b> {tb.unit}</span>
                                    <span>Request: <b>{fmtDay(qty)}</b></span>
                                    <span className={after < 0 ? "text-red-700" : ""}>After approval: <b>{fmtDay(after)}</b></span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button size="sm" onClick={() => openDecision(r, "approve")} data-testid={`btn-approve-${r.id}`} className="h-8 bg-emerald-700 hover:bg-emerald-800">
                                  <Check className="w-3.5 h-3.5 mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openDecision(r, "info")} data-testid={`btn-info-${r.id}`} className="h-8">
                                  <HelpCircle className="w-3.5 h-3.5 mr-1" /> Info
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openDecision(r, "reject")} data-testid={`btn-reject-${r.id}`} className="h-8 text-red-700">
                                  <X className="w-3.5 h-3.5 mr-1" /> Reject
                                </Button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </TabsContent>
              )}

              {/* TEAM CALENDAR */}
              {canApprove && (
                <TabsContent value="team-calendar" className="pt-4" data-testid="tabc-team-calendar">
                  <LeaveCalendarPanel scope="team" />
                </TabsContent>
              )}

              {/* BALANCES (manager+) */}
              {canApprove && (
                <TabsContent value="balances" className="pt-4" data-testid="tabc-balances">
                  <BalancesPanel teamBalances={teamBalances} onReload={load} />
                </TabsContent>
              )}
            </Tabs>
          </>
        )}
      </div>

      {/* Request dialog */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle>Request leave</DialogTitle>
            <DialogDescription>Your request will go to your manager. AYOBELL calculates working days automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Leave type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="request-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}{t.deducts_from_annual ? "" : " (does not deduct)"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="request-start" /></div>
              <div><Label>To</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="request-end" /></div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked, end_date: e.target.checked ? form.start_date : form.end_date })} data-testid="request-half-day" id="half-day-check" />
              <label htmlFor="half-day-check" className="text-sm">Half day</label>
              {form.half_day && (
                <Select value={form.half_day_period} onValueChange={(v) => setForm({ ...form, half_day_period: v })}>
                  <SelectTrigger className="h-8 w-24" data-testid="request-half-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="am">AM</SelectItem>
                    <SelectItem value="pm">PM</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="text-xs text-slate-600" data-testid="request-days-preview">
              <Info className="w-3 h-3 inline mr-1" /> Requested duration: <b>{fmtDay(form.half_day ? 0.5 : requestedDays)}</b> {balance?.unit || "days"}
            </div>
            <div><Label>Reason / notes</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="request-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="btn-submit-request" className="bg-slate-900 hover:bg-slate-800">{saving ? "Submitting…" : "Submit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decision dialog */}
      <Dialog open={decision.open} onOpenChange={(o) => setDecision({ ...decision, open: o })}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{decision.action === "info" ? "Request more info" : decision.action}</DialogTitle>
            <DialogDescription>
              {decision.req && `${decision.req.staff_name || decision.req.staff_email} · ${decision.req.start_date} → ${decision.req.end_date}`}
            </DialogDescription>
          </DialogHeader>
          {decision.action === "approve" && decision.conflicts && decision.conflicts.count > 0 && (
            <div className="border border-amber-300 bg-amber-50 text-amber-900 p-3 text-xs space-y-1" data-testid="rota-conflict-warning">
              <div className="font-semibold inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Rota Conflict</div>
              <div>This staff member has <b>{decision.conflicts.count}</b> scheduled shift{decision.conflicts.count === 1 ? "" : "s"} during the requested leave period. Existing shifts will not be deleted — please review the rota before approving.</div>
            </div>
          )}
          {decision.action === "approve" && decision.req && (
            <div className="text-xs text-slate-600" data-testid="staffing-warning">
              <ShieldAlert className="w-3 h-3 inline mr-1" /> Staffing Warning · Approving this leave may reduce staffing levels at this site.
            </div>
          )}
          <div>
            <Label>{decision.action === "info" ? "What extra information do you need?" : "Notes (optional)"}</Label>
            <Textarea rows={3} value={decision.notes} onChange={(e) => setDecision({ ...decision, notes: e.target.value })} data-testid="decision-notes" />
          </div>
          {decision.action === "approve" && (
            <label className="text-xs inline-flex items-center gap-2">
              <input type="checkbox" checked={decision.override} onChange={(e) => setDecision({ ...decision, override: e.target.checked })} data-testid="decision-override" />
              Override insufficient entitlement (audit-logged)
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision({ ...decision, open: false })}>Cancel</Button>
            <Button onClick={submitDecision} data-testid="btn-decision-submit" className={
              decision.action === "approve" ? "bg-emerald-700 hover:bg-emerald-800"
                : decision.action === "reject" ? "bg-red-700 hover:bg-red-800"
                : "bg-sky-700 hover:bg-sky-800"
            }>
              {decision.action === "approve" ? "Approve" : decision.action === "reject" ? "Reject" : "Send question"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Simple month calendar view for leave (approved + pending)
// -----------------------------------------------------------------------------

function LeaveCalendarPanel({ scope }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const start = `${month}-01`;
    const [y, m] = month.split("-").map(Number);
    const endDate = new Date(y, m, 0).toISOString().slice(0, 10);
    setLoading(true);
    api.get("/leave/calendar", { params: { start, end: endDate, scope } })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [month, scope]);

  const [y, m] = month.split("-").map(Number);
  const firstOfMonth = new Date(y, m - 1, 1);
  const daysInMonth = new Date(y, m, 0).getDate();
  const startDow = (firstOfMonth.getDay() + 6) % 7; // Mon-first
  const cells = Array.from({ length: startDow }, () => null).concat(
    Array.from({ length: daysInMonth }, (_, i) => new Date(y, m - 1, i + 1))
  );

  const forDay = (d) => {
    const iso = d.toISOString().slice(0, 10);
    return items.filter((it) => (it.start_date || "") <= iso && (it.end_date || "") >= iso);
  };

  return (
    <div className="bg-white border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
          <CalendarDays className="w-3 h-3" /> {scope === "team" ? "Team leave" : "My leave"} · {firstOfMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-7" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="cal-prev">‹</Button>
          <Button size="sm" variant="outline" className="h-7" onClick={() => setMonth(shiftMonth(month, +1))} data-testid="cal-next">›</Button>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold text-center py-1">{d}</div>
          ))}
          {cells.map((d, i) => (
            <div key={i} className={`min-h-16 border border-slate-100 p-1 text-xs ${!d ? "bg-slate-50" : ""}`}>
              {d && (
                <>
                  <div className="text-[11px] text-slate-500">{d.getDate()}</div>
                  {forDay(d).slice(0, 3).map((it) => (
                    <div
                      key={it.id}
                      className={`mt-0.5 truncate px-1 py-0.5 border text-[10px] ${it.status === "approved" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}
                      title={`${it.staff_name || ""} · ${it.leave_type || it.kind || ""} (${it.status})`}
                    >
                      {(it.staff_name || "").split(" ")[0] || (it.leave_type || it.kind)}
                    </div>
                  ))}
                  {forDay(d).length > 3 && <div className="text-[9px] text-slate-500">+{forDay(d).length - 3} more</div>}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function shiftMonth(monthStr, delta) {
  const [y, m] = monthStr.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// -----------------------------------------------------------------------------
// Leave Balances panel — includes CSV export + bulk apply of a working-pattern
// -----------------------------------------------------------------------------

function BalancesPanel({ teamBalances, onReload }) {
  const [selected, setSelected] = useState({});
  const [patterns, setPatterns] = useState([]);
  const [patternId, setPatternId] = useState("");
  const [applying, setApplying] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/leave/patterns").then((r) => setPatterns(r.data || [])).catch(() => {});
  }, []);

  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (selectedIds.length === teamBalances.length) setSelected({});
    else setSelected(Object.fromEntries(teamBalances.map((b) => [b.staff_user_id, true])));
  };

  const applyPattern = async () => {
    if (!patternId || selectedIds.length === 0) {
      toast.error("Pick a template and at least one staff member");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post(`/leave/patterns/${patternId}/apply`, { staff_user_ids: selectedIds });
      toast.success(`Pattern applied to ${r.data.applied} staff${r.data.blocked?.length ? ` (${r.data.blocked.length} skipped)` : ""}`);
      setSelected({});
      await onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  const exportCsv = async () => {
    setDownloading(true);
    const id = toast.loading("Preparing CSV…");
    try {
      const r = await api.get("/leave/team-balances/csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ayobell-leave-balances-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded", { id });
    } catch (e) { toast.error(formatApiError(e), { id }); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-3" data-testid="balances-panel">
      <div className="bg-white border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-56">
          <Label className="text-[10px]">Working-pattern template</Label>
          <Select value={patternId} onValueChange={setPatternId}>
            <SelectTrigger className="h-9" data-testid="pattern-select"><SelectValue placeholder="Choose a template" /></SelectTrigger>
            <SelectContent>
              {patterns.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={applyPattern} disabled={applying || !patternId || selectedIds.length === 0} data-testid="btn-apply-pattern" className="h-9 bg-slate-900 hover:bg-slate-800">
          {applying ? "Applying…" : `Apply to ${selectedIds.length || 0} selected`}
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={downloading} data-testid="btn-export-csv" className="h-9">
            <ClipboardList className="w-3.5 h-3.5 mr-1" /> {downloading ? "Downloading…" : "Export CSV"}
          </Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="balances-table">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="px-3 py-2 w-8"><input type="checkbox" checked={teamBalances.length > 0 && selectedIds.length === teamBalances.length} onChange={toggleAll} data-testid="chk-all" /></th>
              <th className="px-3 py-2 text-left">Staff</th>
              <th className="px-3 py-2 text-right">Entitlement</th>
              <th className="px-3 py-2 text-right">Carried</th>
              <th className="px-3 py-2 text-right">Taken</th>
              <th className="px-3 py-2 text-right">Booked</th>
              <th className="px-3 py-2 text-right">Pending</th>
              <th className="px-3 py-2 text-right">Available</th>
              <th className="px-3 py-2 text-right">If pending approved</th>
            </tr>
          </thead>
          <tbody>
            {teamBalances.map((b) => (
              <tr key={b.staff_user_id} className="border-t border-slate-100" data-testid={`balance-row-${b.staff_user_id}`}>
                <td className="px-3 py-2"><input type="checkbox" checked={!!selected[b.staff_user_id]} onChange={() => toggle(b.staff_user_id)} data-testid={`chk-${b.staff_user_id}`} /></td>
                <td className="px-3 py-2">{b.staff_name}<span className="ml-2 text-[10px] text-slate-500 uppercase tracking-widest">{b.role}</span></td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtDay(b.annual_entitlement)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtDay(b.carried_over)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtDay(b.taken)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtDay(b.booked)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmtDay(b.pending)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtDay(b.available)} <span className="text-[10px] uppercase tracking-widest text-slate-500">{b.unit}</span></td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtDay(b.projected_available)}</td>
              </tr>
            ))}
            {teamBalances.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-4 text-center text-slate-500">No team members visible.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
